/**
 * The LinkedIn link: one reusable booking URL per First Interview link, for InMails.
 *
 * Mirrors proxy-server/lib/linkedinShare.js — the stage it exists for, who a link is for, and the
 * daily cap options. The proxy validates all three; these copies only decide what the form offers,
 * so keep both in step.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** Lever's First Interview stage — the only one a LinkedIn link exists for. */
export const FIRST_INTERVIEW_STAGE_ID = 'c59740e3-36e4-4263-8cde-5fe65ef308b4';

export type LinkAudience = 'both' | 'linkedin';
export const DEFAULT_AUDIENCE: LinkAudience = 'both';
export const LINKEDIN_CAP_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 30];
export const DEFAULT_LINKEDIN_DAILY_CAP = 10;

export const isFirstInterviewStage = (stageId?: string | null) =>
  (stageId || '').trim().toLowerCase() === FIRST_INTERVIEW_STAGE_ID;

/**
 * The LinkedIn half of an interview link. All optional: a link saved before the feature has none,
 * and an ABSENT field is one the proxy keeps as stored (or defaults: both audiences, 10 a day).
 */
export interface LinkShareFields {
  /** 'both' = applicants AND LinkedIn (default); 'linkedin' = never offered to an applicant. */
  audience?: LinkAudience;
  /** Most LinkedIn bookings this link takes per Berlin day. */
  linkedinDailyCap?: number;
  /**
   * The public URL for InMails, or null when not minted yet (the Copy button mints it). Read-only:
   * the proxy never accepts it back, and never sends the raw token.
   */
  shareUrl?: string | null;
}

/**
 * Get the link's LinkedIn URL, minting it on first use — or, with `regenerate`, REPLACE it so the
 * old URL stops working. Bookings already made through the old one are unaffected.
 */
export async function ensureShareLink(linkId: string, { regenerate = false } = {}): Promise<string> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/${encodeURIComponent(linkId)}/share-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ regenerate }),
    }
  );
  const body = (await res.json().catch(() => ({}))) as { shareUrl?: string; error?: string };
  if (!res.ok || !body.shareUrl) throw new Error(body.error || 'Could not get the LinkedIn link');
  return body.shareUrl;
}

/** A TA manager's own screening link (Oct 24 2026) — no role, booked from the InMail. */
export const isScreeningLink = (link: { kind?: string | null }): boolean => link.kind === 'screening';

/**
 * The caller's screening link, made with the defaults when they have none (POST
 * /api/interview-links/screening/mine). `reactivate` switches a closed one back on — the page's
 * own call on open never does. The link comes back in the shape the links list has.
 */
export async function ensureMyScreeningLink<T = unknown>(
  reactivate = false
): Promise<{ link: T; created: boolean; reactivated: boolean }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/interview-links/screening/mine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reactivate }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || 'Could not create your screening link');
  }
  return (await res.json()) as { link: T; created: boolean; reactivated: boolean };
}
